/**
 * MovementSystem — pathfinding wrapper.
 *
 * Uses NavmeshSystem (continuous world-pixel paths) when the navmesh has been
 * built. Falls back to EasyStar tile-grid A* otherwise, converting tile coords
 * to world-pixel centres so callers always receive world-pixel waypoints.
 *
 * All public coordinates are in **world pixels**.
 */

import { js as EasyStar } from 'easystarjs';
import { NavmeshSystem } from './NavmeshSystem';
import type { WorldPoint } from './NavmeshSystem';

export type { WorldPoint };

export class MovementSystem {
  private readonly tileSize: number;
  private easystar: EasyStar;
  private navmesh: NavmeshSystem | null = null;
  private gridCols: number;
  private gridRows: number;
  private grid: number[][];

  constructor(grid: number[][], tileSize = 32) {
    this.tileSize = tileSize;
    this.grid = grid;
    this.easystar = new EasyStar();
    this.easystar.setGrid(grid);
    this.easystar.setAcceptableTiles([0]);
    this.easystar.enableDiagonals();
    this.easystar.disableCornerCutting();
    this.gridRows = grid.length;
    this.gridCols = grid.length > 0 ? grid[0].length : 0;
  }

  /**
   * Build the navmesh from the walkability grid.
   * Call once after construction; updateGrid() handles subsequent rebuilds.
   */
  buildNavmesh(walkabilityGrid: number[][]): void {
    if (!this.navmesh) this.navmesh = new NavmeshSystem();
    this.navmesh.build(walkabilityGrid, this.tileSize);
  }

  /** Use pre-computed walkable polygons instead of rebuilding from the grid. */
  buildNavmeshFromPolygons(walkablePolygons: WorldPoint[][]): void {
    if (!this.navmesh) this.navmesh = new NavmeshSystem();
    this.navmesh.buildFromPolygons(walkablePolygons);
  }

  /** Replace the walkability grid (e.g. after a barrel or building is destroyed). */
  updateGrid(grid: number[][]): void {
    this.grid = grid;
    this.easystar.setGrid(grid);
    this.gridRows = grid.length;
    this.gridCols = grid.length > 0 ? grid[0].length : 0;
    this.navmesh?.build(grid, this.tileSize);
  }

  /** Returns true if the world-pixel position sits on a walkable tile (value 0). */
  isWalkable(worldX: number, worldY: number): boolean {
    const tileX = Math.floor(worldX / this.tileSize);
    const tileY = Math.floor(worldY / this.tileSize);
    if (tileX < 0 || tileY < 0 || tileX >= this.gridCols || tileY >= this.gridRows) return false;
    return this.grid[tileY][tileX] === 0;
  }

  /**
   * Find a path between two world-pixel positions.
   *
   * Prefers the navmesh for smooth, sub-tile-precise paths.
   * Falls back to EasyStar (snapped to tile centres) when the navmesh is not ready.
   *
   * Callback receives an array of world-pixel waypoints including both the start
   * and end positions, or null if no path exists.
   */
  findPath(
    from: WorldPoint,
    to: WorldPoint,
    callback: (path: WorldPoint[] | null) => void,
  ): void {
    // ── Navmesh (preferred) ───────────────────────────────────────────────────
    if (this.navmesh?.isReady) {
      callback(this.navmesh.findPath(from, to));
      return;
    }

    // ── EasyStar fallback ─────────────────────────────────────────────────────
    const T = this.tileSize;
    const fromTile = { x: Math.floor(from.x / T), y: Math.floor(from.y / T) };
    const toTile   = { x: Math.floor(to.x   / T), y: Math.floor(to.y   / T) };

    if (!this.isInBounds(fromTile) || !this.isInBounds(toTile)) {
      callback(null);
      return;
    }

    this.easystar.findPath(fromTile.x, fromTile.y, toTile.x, toTile.y, (tilePath) => {
      if (!tilePath || tilePath.length === 0) { callback(null); return; }
      // Return tile centres as world-pixel coordinates
      callback(tilePath.map((tp) => ({
        x: tp.x * T + T / 2,
        y: tp.y * T + T / 2,
      })));
    });
    this.easystar.calculate();
  }

  private isInBounds(tile: WorldPoint): boolean {
    return tile.x >= 0 && tile.y >= 0 && tile.x < this.gridCols && tile.y < this.gridRows;
  }
}
